import { ShoppingCart, Heart, Home, Shirt, Gem, Car, Monitor, Gift, Watch, Building, Plane, Globe, ArrowRight, Footprints, PawPrint, FileStack, Wallet, Music, Wine } from "lucide-react";
import { useState, useEffect, useRef } from "react";

const Markets = () => {
  const [selectedMarket, setSelectedMarket] = useState("Retailer");
  const [isMobile, setIsMobile] = useState(false);
  const [showDetails, setShowDetails] = useState(true);
  const marketDetailsRef = useRef<HTMLDivElement>(null);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Define the different markets and their sites
  const markets = [
    {
      id: "Retailer",
      name: "Retailer",
      icon: <ShoppingCart size={24} />,
      color: "from-blue-600 to-blue-700",
      sites: ["Carrefour.com", "Walmart.com", "Target.com", "Tesco.com", "Amazon.com", "Costco.com", "Lidl.com"],
      description: "Complete data collection from major retailers, including product catalogs, prices, promotions, and stock levels. Updated daily."
    },
    {
      id: "WineSpirits",
      name: "Wine & Spirits",
      icon: <Wine size={24} />,
      color: "from-purple-700 to-red-700",
      sites: ["Vivino.com", "Wine-searcher.com", "Totalwine.com", "Winespectator.com", "Whiskyadvocate.com", "Masterofmalt.com", "Winedirect.com", "Drizly.com"],
      description: "Comprehensive wine and spirits market data including bottles, vintages, ratings, pricing and availability from leading retailers and community platforms."
    },
    {
      id: "Beauty",
      name: "Beauty, Health & Pharma",
      icon: <Heart size={24} />,
      color: "from-pink-600 to-pink-700",
      sites: ["Sephora.com", "Boots.com", "Ulta.com", "LookFantastic.com", "Glossier.com", "Feelunique.com", "Superdrug.com", "Lorealparis.com"],
      description: "Comprehensive beauty and health product data including ingredients, prices, availability, and customer reviews across all major beauty retailers."
    },
    {
      id: "Home",
      name: "Home Deco & DIY",
      icon: <Home size={24} />,
      color: "from-green-600 to-green-700",
      sites: ["Ikea.com", "Homedepot.com", "Wayfair.com", "Williams-sonoma.com", "Potterybarn.com", "Crateandbarrel.com", "Westelm.com", "Anthropologie.com"],
      description: "Full coverage of home decoration and DIY markets with product specifications, prices, and availability from leading retailers."
    },
    {
      id: "Fashion",
      name: "Fashion",
      icon: <Shirt size={24} />,
      color: "from-purple-600 to-purple-700",
      sites: ["Macys.com", "Uniqlo.com", "Zara.com", "Shein.com", "Asos.com", "Zalando.com", "Mango.com", "Nordstrom.com"],
      description: "Fashion industry intelligence with product data, pricing strategies, collection launches, and inventory levels from top clothing retailers."
    },
    {
      id: "Luxury",
      name: "Luxury",
      icon: <Gem size={24} />,
      color: "from-amber-600 to-amber-700",
      sites: ["Dior.com", "Gucci.com", "Louisvuitton.com", "Chanel.com", "Hermes.com", "Burberry.com", "Prada.com", "Balenciaga.com"],
      description: "Exclusive luxury market data covering premium brands, pricing position, product availability, and new collection launches."
    },
    {
      id: "Sport",
      name: "Sport",
      icon: <Footprints size={24} />,
      color: "from-red-600 to-red-700",
      sites: ["Nike.com", "Adidas.com", "Underarmour.com", "Decathlon.com", "Patagonia.com", "Thenorthface.com", "Asics.com"],
      description: "Sports equipment and apparel market intelligence with complete product catalogs, technical specifications, and pricing data."
    },
    {
      id: "Auto",
      name: "Auto",
      icon: <Car size={24} />,
      color: "from-gray-600 to-gray-700",
      sites: ["Edmunds.com", "Autotrader.com", "Kbb.com", "Cars.com", "Carmax.com", "Carvana.com", "Truecar.com", "Autozone.com"],
      description: "Automotive market data including vehicle listings, pricing trends, specifications, and availability across major platforms."
    },
    {
      id: "Electronics",
      name: "Electronics",
      icon: <Monitor size={24} />,
      color: "from-cyan-600 to-cyan-700",
      sites: ["Bestbuy.com", "Apple.com", "Samsung.com", "Dell.com", "Hp.com", "Newegg.com", "Sony.com"],
      description: "Consumer electronics market coverage with product specifications, pricing, promotions, and stock levels from leading retailers."
    },
    {
      id: "Kids",
      name: "Kids and Toys",
      icon: <Gift size={24} />,
      color: "from-orange-600 to-orange-700",
      sites: ["Lego.com", "Toysrus.com", "Disneystore.com", "Mattel.com", "Hasbro.com", "Gap.com", "Target.com"],
      description: "Kids products and toys market data including new releases, pricing, availability, and seasonal trends from major manufacturers and retailers."
    },
    {
      id: "Jewelry",
      name: "Jewelry & Watches",
      icon: <Watch size={24} />,
      color: "from-yellow-600 to-yellow-700",
      sites: ["Tiffany.com", "Cartier.com", "Omegawatches.com", "Swarovski.com", "Pandora.com", "Bluenile.com", "Davidyurman.com", "Jared.com"],
      description: "Jewelry and watches market intelligence with product catalogs, pricing, limited editions, and availability data from leading brands."
    },
    {
      id: "RealEstate",
      name: "Real Estate",
      icon: <Building size={24} />,
      color: "from-indigo-600 to-indigo-700",
      sites: ["Zillow.com", "Realtor.com", "Redfin.com", "Trulia.com", "Century21.com", "Coldwellbanker.com", "Sothebysrealty.com"],
      description: "Real estate market data including property listings, pricing trends, availability, and geographical insights from major platforms."
    },
    {
      id: "Booking",
      name: "Online Booking",
      icon: <Plane size={24} />,
      color: "from-teal-600 to-teal-700",
      sites: ["Booking.com", "Expedia.com", "Hotels.com", "Airbnb.com", "Tripadvisor.com", "Kayak.com", "Priceline.com", "Agoda.com"],
      description: "Travel and hospitality industry intelligence with accommodation pricing, availability, and booking trends from leading platforms."
    },
    {
      id: "Pets",
      name: "Pets Animal",
      icon: <PawPrint size={24} />,
      color: "from-lime-600 to-lime-700",
      sites: ["Chewy.com", "Petsmart.com", "Petco.com", "Barkbox.com", "Purina.com", "Royalcanin.com", "1800petmeds.com", "Petfinder.com"],
      description: "Pet products market intelligence covering food, toys, accessories, and healthcare products from major pet retailers and subscription services."
    },
    {
      id: "OfficeSupplies",
      name: "Office Supplies",
      icon: <FileStack size={24} />,
      color: "from-emerald-600 to-emerald-700",
      sites: ["Staples.com", "Officedepot.com", "Quill.com", "Uline.com", "Officemax.com", "Grainger.com", "Mcmaster.com"],
      description: "Complete data collection on office supplies including paper products, writing tools, organization items, and office consumables from major retailers."
    },
    {
      id: "Finance",
      name: "Finance",
      icon: <Wallet size={24} />,
      color: "from-green-500 to-blue-500",
      sites: ["Bloomberg.com", "Wsj.com", "Cnbc.com", "Investing.com", "Marketwatch.com", "Tradingview.com", "Ft.com", "Morningstar.com"],
      description: "Comprehensive financial market data including stock prices, financial news articles, market analysis, and investment insights from leading financial platforms."
    },
    {
      id: "Entertainment",
      name: "Entertainment",
      icon: <Music size={24} />,
      color: "from-red-500 to-purple-500",
      sites: ["Spotify.com", "Youtube.com", "Netflix.com", "Disneyplus.com", "Hulu.com", "Twitch.tv", "Primevideo.com", "Hbomax.com"],
      description: "Entertainment industry data including streaming services, content platforms, pricing models, and user engagement metrics from major entertainment providers."
    }
  ];

  // Find the selected market
  const currentMarket = markets.find(market => market.id === selectedMarket) || markets[0];

  // Function to handle market selection
  const handleMarketSelect = (marketId: string) => {
    if (selectedMarket === marketId) {
      // Toggle details visibility if clicking the same market
      setShowDetails(!showDetails);
    } else {
      // Show details when selecting a different market
      setSelectedMarket(marketId);
      setShowDetails(true);
    }
    
    // Always scroll to the market details section when clicking a market
    if (marketDetailsRef.current) {
      setTimeout(() => {
        marketDetailsRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'nearest'
        });
      }, 100);
    }
  };

  // Function to get favicon URL for a site
  const getFaviconUrl = (domain: string) => {
    return `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
  };

  // Function to handle redirects to Benchr login
  const handleBenchrLogin = (e: React.MouseEvent) => {
    e.preventDefault();
    // Open the calendar appointment URL directly instead of the login page
    window.open("https://calendar.google.com/calendar/appointments/schedules/AcZssZ0ixREziCGTZSnjXhuPB0TBIti2X5Ks-ATre2wgAkuMuHqJ4XWW7hldUQxV7WW_GMwHwd9IwfBm?gv=true", "_blank", "noopener,noreferrer");
  };

  return (
    <section id="markets" className="py-24 relative overflow-hidden bg-benchr-50 dark:bg-benchr-900/60">
      {/* Background elements */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-benchr-400/5 rounded-full blur-3xl -z-10" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-benchr-600/5 rounded-full blur-3xl -z-10" />
      
      <div className="container max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-benchr-900 dark:text-white mb-4 tracking-tight animate-slide-up">
            <span className="text-gradient">Unlock Any E-commerce Data</span> Worldwide
          </h2>
          <p className="text-lg text-benchr-600 dark:text-benchr-300 animate-slide-up" style={{ animationDelay: "100ms" }}>
            We provide comprehensive e-commerce data from any website worldwide. Our data feeds include complete product, pricing, and market data ready for your analysis.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-10">
          {markets.map((market) => (
            <button 
              key={market.id}
              onClick={() => handleMarketSelect(market.id)}
              className={`flex flex-col items-center p-4 rounded-xl transition-all duration-300 ${
                selectedMarket === market.id 
                ? "bg-white dark:bg-benchr-800 shadow-lg scale-105 border border-benchr-200 dark:border-benchr-700" 
                : "bg-white/60 dark:bg-benchr-900/60 hover:bg-white hover:shadow-md hover:scale-105 dark:hover:bg-benchr-800/60 border border-transparent hover:border-benchr-100 dark:hover:border-benchr-700/50"
              }`}
            >
              <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${market.color} flex items-center justify-center text-white mb-3`}>
                {market.icon}
              </div>
              <h4 className="text-sm font-medium text-benchr-900 dark:text-white text-center">
                {market.name}
              </h4>
            </button>
          ))}
        </div>

        {/* Market Details Section with Animation */}
        <div 
          id="market-details" 
          ref={marketDetailsRef}
          className={`bg-white dark:bg-benchr-800/40 rounded-2xl shadow-lg overflow-hidden transition-all duration-500 ease-in-out ${
            showDetails ? "opacity-100 max-h-[2000px]" : "opacity-0 max-h-0"
          }`}
        >
          {showDetails && (
            <div className="p-6 md:p-8">
              <div className="flex flex-col md:flex-row gap-8">
                <div className="md:w-2/3">
                  <div className="flex items-center mb-4">
                    <div className={`w-10 h-10 rounded-full bg-gradient-to-r ${currentMarket.color} flex items-center justify-center text-white mr-3`}>
                      {currentMarket.icon}
                    </div>
                    <h3 className="text-2xl font-bold text-benchr-900 dark:text-white">
                      {currentMarket.name} Market
                    </h3>
                  </div>
                  
                  <p className="text-benchr-600 dark:text-benchr-300 mb-6 text-lg">
                    {currentMarket.description}
                  </p>
                  
                  <div className="flex flex-wrap gap-3 mb-6">
                    {currentMarket.sites.map((site, index) => (
                      <div 
                        key={index}
                        className="flex items-center py-2 px-4 rounded-full bg-benchr-50 dark:bg-benchr-700/30 border border-benchr-100 dark:border-benchr-700"
                      >
                        <img 
                          src={getFaviconUrl(site)} 
                          alt={`${site} favicon`} 
                          className="w-4 h-4 mr-2" 
                        />
                        <span className="text-benchr-800 dark:text-benchr-200 font-medium text-sm">
                          {site}
                        </span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="flex flex-wrap gap-4">
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        window.open(
                          'https://calendar.google.com/calendar/appointments/schedules/AcZssZ0ixREziCGTZSnjXhuPB0TBIti2X5Ks-ATre2wgAkuMuHqJ4XWW7hldUQxV7WW_GMwHwd9IwfBm?gv=true',
                          'BookADemo',
                          'width=1400,height=900'
                        );
                      }}
                      className="group inline-flex items-center justify-center px-6 py-3.5 bg-[#1e70d1] hover:bg-[#1e70d1]/90 text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-all duration-300 ease-in-out w-full sm:w-auto pulse-soft"
                    >
                      Book a Demo
                      <ArrowRight size={18} className="ml-2 transition-transform group-hover:translate-x-1" />
                    </a>
                  </div>
                </div>
                
                <div className="md:w-1/3 bg-benchr-50 dark:bg-benchr-900/50 rounded-xl p-5">
                  <h4 className="text-lg font-semibold text-benchr-900 dark:text-white mb-4 flex items-center">
                    <Globe className="w-5 h-5 mr-2 text-benchr-600 dark:text-benchr-400" />
                    Coverage Highlights
                  </h4>
                  
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center mt-0.5 mr-3 flex-shrink-0">
                        <div className="w-2 h-2 rounded-full bg-green-500"></div>
                      </div>
                      <span className="text-sm text-benchr-700 dark:text-benchr-300">Complete product catalogs</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center mt-0.5 mr-3 flex-shrink-0">
                        <div className="w-2 h-2 rounded-full bg-green-500"></div>
                      </div>
                      <span className="text-sm text-benchr-700 dark:text-benchr-300">Competitive pricing intelligence</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center mt-0.5 mr-3 flex-shrink-0">
                        <div className="w-2 h-2 rounded-full bg-green-500"></div>
                      </div>
                      <span className="text-sm text-benchr-700 dark:text-benchr-300">Daily updates and historical data</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center mt-0.5 mr-3 flex-shrink-0">
                        <div className="w-2 h-2 rounded-full bg-green-500"></div>
                      </div>
                      <span className="text-sm text-benchr-700 dark:text-benchr-300">Market trends and analytics</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center mt-0.5 mr-3 flex-shrink-0">
                        <div className="w-2 h-2 rounded-full bg-green-500"></div>
                      </div>
                      <span className="text-sm text-benchr-700 dark:text-benchr-300">Customer review tracking</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Markets;

